// /demo — corp demo screen for SoftBank + KDDI prospects.
//
// Self-contained: replays a scripted DemoScript locally, no backend
// needed. Renders the SAME visual layout as /triton/graph (chat
// surface left, agent-graph DAG right, carrier chips on top) plus
// three ending panels (Receipt · Twin Update · Pivot) that slide in
// after the negotiation seals.
//
// Carrier toggle in the picker swaps PayPay ↔ Coincheck on the fly
// so the prospect sees the same demo render with their own brand.

import type { JSX, ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  type GraphEdgeEvent,
  type GraphNodeEvent,
  type NodeDefinition,
  type NodeId,
  type NodeState,
  type PactEvent,
  NODE_DEFINITIONS,
  NODES,
} from "../lib/agent-graph-types";
import {
  type AgentTone,
  type CarrierId,
  type CarrierProfile,
  type DemoFrame,
  type DemoScript,
  CARRIERS,
  compileDemoScript,
} from "../lib/demoScript";
import { DEMO_SCRIPTS } from "../lib/demoScripts";
import { useDemoPlayer } from "../lib/useDemoPlayer";


// ── Chat state ──────────────────────────────────────────────────────


interface ChatMessage {
  id: string;
  kind: "user" | "assistant" | "thinking" | "agent_dialogue";
  text: string;
  speaker?: string;
  tone?: AgentTone;
  streaming?: boolean;
}


interface ChatState {
  messages: ChatMessage[];
  streamingId: string | null;
}


type ChatAction =
  | { kind: "user_message"; seq: number; text: string }
  | { kind: "thinking"; seq: number; text: string }
  | { kind: "agent_say"; seq: number; speaker: string; tone: AgentTone; text: string }
  | { kind: "assistant_start"; seq: number }
  | { kind: "chunk"; text: string }
  | { kind: "assistant_end" }
  | { kind: "turn_completed"; final_text: string }
  | { kind: "reset" };


function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.kind) {
    case "reset":
      return { messages: [], streamingId: null };
    case "user_message":
      return {
        ...state,
        messages: [
          ...state.messages,
          { id: `u${action.seq}`, kind: "user", text: action.text },
        ],
      };
    case "thinking":
      return {
        ...state,
        messages: [
          ...state.messages,
          { id: `t${action.seq}`, kind: "thinking", text: action.text },
        ],
      };
    case "agent_say":
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: `d${action.seq}`,
            kind: "agent_dialogue",
            text: action.text,
            speaker: action.speaker,
            tone: action.tone,
          },
        ],
      };
    case "assistant_start": {
      const id = `a${action.seq}`;
      return {
        ...state,
        streamingId: id,
        messages: [
          ...state.messages,
          { id, kind: "assistant", text: "", streaming: true },
        ],
      };
    }
    case "chunk": {
      if (!state.streamingId) return state;
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === state.streamingId ? { ...m, text: m.text + action.text } : m
        ),
      };
    }
    case "assistant_end":
      return {
        ...state,
        streamingId: null,
        messages: state.messages.map((m) =>
          m.streaming ? { ...m, streaming: false } : m
        ),
      };
    case "turn_completed":
      // Replace streamed text with final (handles cases where the
      // chunks were a teaser and final_text is the canonical line).
      // If no chunks were produced, append final_text as a fresh
      // assistant message.
      if (state.messages.some((m) => m.kind === "assistant")) {
        return state;
      }
      return {
        ...state,
        messages: [
          ...state.messages,
          { id: `a-final`, kind: "assistant", text: action.final_text },
        ],
      };
  }
}


// ── Graph state ─────────────────────────────────────────────────────


interface PanelState {
  nodeStates: Record<NodeId, NodeState>;
  nodeLabels: Record<NodeId, string>;
  edgeFires: Array<GraphEdgeEvent & { id: string }>;
  pactEvents: PactEvent[];
}


type PanelAction =
  | { kind: "node"; event: GraphNodeEvent }
  | { kind: "edge"; event: GraphEdgeEvent }
  | { kind: "pact"; event: PactEvent }
  | { kind: "reseed"; carrier: CarrierProfile };


function initialPanelState(carrier: CarrierProfile): PanelState {
  const labels = NODES.reduce((acc, id) => {
    acc[id] = NODE_DEFINITIONS[id].label;
    return acc;
  }, {} as Record<NodeId, string>);
  // Carrier wallet is one node. For KDDI, au PAY and Coincheck are
  // the same KDDI wallet (au PAY for fiat + points, Coincheck for
  // crypto, accessed through the same KDDI account). For SoftBank,
  // it's the PayPay app.
  labels.settlement = carrier.id === "kddi_au"
    ? "au PAY · Coincheck Wallet"
    : "PayPay Wallet";
  labels.pact_broker = "Agent Marketplace";
  const nodeStates = NODES.reduce((acc, id) => {
    acc[id] = "idle";
    return acc;
  }, {} as Record<NodeId, NodeState>);
  return { nodeStates, nodeLabels: labels, edgeFires: [], pactEvents: [] };
}


function panelReducer(state: PanelState, action: PanelAction): PanelState {
  switch (action.kind) {
    case "reseed":
      return initialPanelState(action.carrier);
    case "node":
      return {
        ...state,
        nodeStates: { ...state.nodeStates, [action.event.node_id]: action.event.state },
        nodeLabels: {
          ...state.nodeLabels,
          [action.event.node_id]: action.event.label || state.nodeLabels[action.event.node_id],
        },
      };
    case "edge": {
      const id = `${action.event.from_node_id}->${action.event.to_node_id}-${state.edgeFires.length}`;
      return { ...state, edgeFires: [...state.edgeFires, { ...action.event, id }].slice(-24) };
    }
    case "pact":
      return { ...state, pactEvents: [...state.pactEvents, action.event].slice(-50) };
  }
}


// Map an agent_say's tone to a DAG node for back-and-forth edge
// animation. friend_twin / family_twin rotate across twin_3..6 so
// each multi-Twin demo's friends light different nodes.
function toneToNode(tone: AgentTone, rotor: React.MutableRefObject<number>): NodeId | null {
  switch (tone) {
    case "your_twin":    return "your_twin";
    case "partner_twin": return "partner_twin";
    case "family_twin":
    case "friend_twin": {
      const slots: NodeId[] = ["twin_3", "twin_4", "twin_5", "twin_6"];
      const slot = slots[rotor.current % slots.length] as NodeId;
      rotor.current++;
      return slot;
    }
    case "aristotle":   return "aristotle";
    case "broker":      return "pact_broker";
    case "negotiator":  return "negotiator";
    case "provider":    return "pact_broker"; // providers speak through the marketplace
    case "settlement":  return "settlement";
    case "system":      return "midnight";
  }
  return null;
}


// ── Endings state ───────────────────────────────────────────────────


interface EndingState {
  showReceipt: boolean;
  showTwinUpdate: boolean;
  showPivot: boolean;
}


type EndingPhase = "receipt" | "twin_update" | "pivot";


// ── Component ───────────────────────────────────────────────────────


export function DemoScreen(): JSX.Element {
  const [carrierId, setCarrierId] = useState<CarrierId>("paypay_softbank");
  const [scriptId, setScriptId] = useState<string>(DEMO_SCRIPTS[0]?.id ?? "");
  const [speed, setSpeed] = useState<number>(0.5);

  const carrier = CARRIERS[carrierId];

  const compiledScript = useMemo<DemoScript | null>(() => {
    const base = DEMO_SCRIPTS.find((s) => s.id === scriptId);
    if (!base) return null;
    return compileDemoScript(base, carrier);
  }, [scriptId, carrier]);

  const [chat, dispatchChat] = useReducer(chatReducer, { messages: [], streamingId: null });
  const [panel, dispatchPanel] = useReducer(panelReducer, carrier, initialPanelState);
  const [endings, setEndings] = useState<EndingState>({
    showReceipt: false, showTwinUpdate: false, showPivot: false,
  });

  // Re-seed the graph state with the new carrier labels every time
  // the carrier flips, so flipping mid-replay doesn't leak the old
  // brand on the DAG nodes.
  useEffect(() => {
    dispatchPanel({ kind: "reseed", carrier });
    dispatchChat({ kind: "reset" });
    setEndings({ showReceipt: false, showTwinUpdate: false, showPivot: false });
  }, [carrier]);

  // Track who spoke last so we can fire a back-and-forth arc on the
  // DAG between consecutive speakers' nodes. Cleared on reset/carrier
  // change via onReset closure recomputation.
  const lastSpeakerNodeRef = useRef<NodeId | null>(null);
  // Stable rotor for multi-twin tones: friend_twin gets distributed
  // across twin_3..6 in arrival order so each friend lights a
  // different node. family_twin behaves the same.
  const twinRotorRef = useRef(0);

  // Reset everything when a script is selected fresh / the user hits
  // restart. The player calls this on script change and on restart().
  const onReset = useCallback(() => {
    dispatchChat({ kind: "reset" });
    dispatchPanel({ kind: "reseed", carrier });
    setEndings({ showReceipt: false, showTwinUpdate: false, showPivot: false });
    lastSpeakerNodeRef.current = null;
    twinRotorRef.current = 0;
  }, [carrier]);

  // Wire each demo event to the right reducer / setter.
  const onEvent = useCallback((event: DemoFrame["event"]) => {
    // Chat events
    if (event.topic_class === "chat") {
      switch (event.type) {
        case "chat.user_message":
          dispatchChat({ kind: "user_message", seq: event.seq, text: event.text });
          lastSpeakerNodeRef.current = "triton";
          return;
        case "chat.thinking":
          dispatchChat({ kind: "thinking", seq: (event as { seq: number }).seq,
                         text: (event as { text: string }).text });
          return;
        case "chat.agent_say": {
          const ev = event as {
            seq: number; speaker: string; tone: AgentTone; text: string;
          };
          dispatchChat({
            kind: "agent_say",
            seq: ev.seq, speaker: ev.speaker, tone: ev.tone, text: ev.text,
          });
          // PACT + Midnight is THE protocol stack for every
          // connection. Every agent comm relays through both:
          //   sender → PACT (routes the message)
          //         → Midnight (anchors + authenticates)
          //         → receiver
          // We dispatch three edge events so the viewer sees the
          // message travel through both protocol layers.
          const toNode = toneToNode(ev.tone, twinRotorRef);
          const fromNode = lastSpeakerNodeRef.current;
          const preview = ev.text.slice(0, 40);
          if (toNode && toNode !== "pact_broker" && toNode !== "midnight") {
            const fireEdge = (from: NodeId, to: NodeId, kind: string) => {
              dispatchPanel({
                kind: "edge",
                event: {
                  topic_class: "graph",
                  type: "graph.edge_fire",
                  turn_id: "demo",
                  from_node_id: from,
                  to_node_id: to,
                  kind,
                  payload_preview: preview,
                  metadata: {},
                  at: Date.now(),
                },
              });
            };
            if (fromNode && fromNode !== "pact_broker" && fromNode !== toNode) {
              fireEdge(fromNode, "pact_broker", "pact.send");
            }
            fireEdge("pact_broker", "midnight", "midnight.anchor");
            fireEdge("midnight", toNode, "midnight.deliver");
          }
          lastSpeakerNodeRef.current = toNode ?? lastSpeakerNodeRef.current;
          return;
        }
        case "chat.assistant_message_start":
          dispatchChat({ kind: "assistant_start", seq: event.seq });
          return;
        case "chat.chunk":
          dispatchChat({ kind: "chunk", text: (event as { text: string }).text });
          return;
        case "chat.assistant_message_end":
          dispatchChat({ kind: "assistant_end" });
          return;
        case "chat.turn.completed":
          dispatchChat({ kind: "turn_completed",
                         final_text: (event as { final_text: string }).final_text });
          return;
      }
      return;
    }
    // Graph events
    if (event.topic_class === "graph") {
      if (event.type === "graph.node_state") {
        dispatchPanel({ kind: "node", event });
      } else if (event.type === "graph.edge_fire") {
        dispatchPanel({ kind: "edge", event });
      }
      return;
    }
    if (event.topic_class === "pact") {
      dispatchPanel({ kind: "pact", event });
      return;
    }
    // Demo ending phases
    if (event.topic_class === "demo" && event.type === "demo.ending") {
      const phase = event.phase as EndingPhase;
      setEndings((prev) => ({
        ...prev,
        showReceipt: prev.showReceipt || phase === "receipt",
        showTwinUpdate: prev.showTwinUpdate || phase === "twin_update",
        showPivot: prev.showPivot || phase === "pivot",
      }));
      return;
    }
  }, []);

  const player = useDemoPlayer({
    script: compiledScript,
    onEvent,
    onReset,
    speed,
    autoPlay: false,
  });

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "260px 1fr 480px",
        gap: 0,
        height: "100vh",
        overflow: "hidden",
        background: "#020617",
        color: "#e2e8f0",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      data-carrier={carrierId}
    >
      <style>{`
        @keyframes demo-slide-left {
          from { opacity: 0; transform: translateX(-24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes demo-slide-right {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes demo-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
      <DemoPicker
        scriptId={scriptId}
        carrierId={carrierId}
        speed={speed}
        playState={player.state}
        frameIdx={player.frameIdx}
        frameCount={player.frameCount}
        onSelectScript={setScriptId}
        onSelectCarrier={setCarrierId}
        onSpeed={setSpeed}
        onPlay={player.play}
        onPause={player.pause}
        onRestart={player.restart}
      />
      <main style={{ minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <CarrierChipsHeader carrier={carrier} script={compiledScript} />
        <ChatPanel
          messages={chat.messages}
          userPrompt={compiledScript?.userPrompt ?? ""}
          playState={player.state}
        />
        {(endings.showReceipt || endings.showTwinUpdate || endings.showPivot) && compiledScript && (
          <EndingPanels
            script={compiledScript}
            carrier={carrier}
            showReceipt={endings.showReceipt}
            showTwinUpdate={endings.showTwinUpdate}
            showPivot={endings.showPivot}
          />
        )}
      </main>
      <GraphPanel
        state={panel}
        carrier={carrier}
        usedNodes={compiledScript?.usedNodes ?? null}
      />
    </div>
  );
}


// ── DemoPicker (left rail) ──────────────────────────────────────────


interface DemoPickerProps {
  scriptId: string;
  carrierId: CarrierId;
  speed: number;
  playState: string;
  frameIdx: number;
  frameCount: number;
  onSelectScript: (id: string) => void;
  onSelectCarrier: (id: CarrierId) => void;
  onSpeed: (s: number) => void;
  onPlay: () => void;
  onPause: () => void;
  onRestart: () => void;
}


function DemoPicker(props: DemoPickerProps): JSX.Element {
  return (
    <aside
      style={{
        background: "#0b1220",
        borderRight: "1px solid #1e293b",
        padding: 16,
        display: "flex", flexDirection: "column", gap: 14,
        height: "100vh",
        minHeight: 0,
        overflow: "hidden",
      }}
      aria-label="Demo picker"
    >
      <div style={{ flexShrink: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, marginBottom: 4 }}>
          Foundation Lifestyle
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", letterSpacing: 0.5, textTransform: "uppercase" }}>
          Corp Demo · v1.2 substrate
        </div>
      </div>

      <section style={{ flexShrink: 0 }}>
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.6 }}>
          Carrier
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {(Object.keys(CARRIERS) as CarrierId[]).map((id) => (
            <button
              key={id}
              onClick={() => props.onSelectCarrier(id)}
              style={{
                flex: 1, padding: "8px 6px",
                background: props.carrierId === id ? "#2563eb" : "#1e293b",
                color: "#f8fafc", border: "none", borderRadius: 6,
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                lineHeight: 1.2,
              }}
            >
              {id === "paypay_softbank" ? "SoftBank · PayPay" : "KDDI · au + Coincheck"}
            </button>
          ))}
        </div>
      </section>

      <section style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 6, overflow: "hidden" }}>
        <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6, flexShrink: 0 }}>
          Demos · 11
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, overflowY: "auto", flex: 1, minHeight: 0 }}>
          {DEMO_SCRIPTS.map((s, idx) => {
            const needsKddi = s.carrierFit === "kddi" && props.carrierId !== "kddi_au";
            const needsSoftbank = s.carrierFit === "softbank" && props.carrierId !== "paypay_softbank";
            const isSelected = s.id === props.scriptId;
            return (
              <button
                key={s.id}
                onClick={() => {
                  // Auto-flip the carrier when picking a carrier-specific
                  // demo — better than greying it out and confusing the
                  // viewer about why they can't click.
                  if (needsKddi) props.onSelectCarrier("kddi_au");
                  if (needsSoftbank) props.onSelectCarrier("paypay_softbank");
                  props.onSelectScript(s.id);
                }}
                style={{
                  textAlign: "left", padding: "8px 10px",
                  background: isSelected ? "#1d4ed8" : "#0f172a",
                  color: "#f8fafc",
                  border: isSelected ? "1px solid #60a5fa" : "1px solid #1e293b",
                  borderRadius: 6, fontSize: 13, cursor: "pointer",
                  flexShrink: 0,
                }}
                title={
                  needsKddi ? "KDDI-only demo — will switch carrier to KDDI on click" :
                  needsSoftbank ? "SoftBank-only demo — will switch carrier on click" :
                  s.subtitle
                }
              >
                <div style={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {idx + 1}. {s.title}
                  {needsKddi && <span style={{ marginLeft: 6, fontSize: 10, color: "#fbbf24" }}>· KDDI only</span>}
                  {needsSoftbank && <span style={{ marginLeft: 6, fontSize: 10, color: "#fbbf24" }}>· SoftBank only</span>}
                </div>
                <div style={{ fontSize: 11, color: isSelected ? "#dbeafe" : "#94a3b8", marginTop: 2 }}>
                  {s.killerFeatures[0]} · ~{s.totalSeconds}s
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section style={{ borderTop: "1px solid #1e293b", paddingTop: 12, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {props.playState !== "playing" ? (
            <button
              onClick={props.onPlay}
              style={{
                flex: 1, padding: "10px 10px",
                background: "#16a34a", color: "#f8fafc",
                border: "none", borderRadius: 6, fontSize: 15,
                fontWeight: 700, cursor: "pointer",
              }}
            >
              {props.playState === "done" ? "↻ Play again" : "▶ Play"}
            </button>
          ) : (
            <button
              onClick={props.onPause}
              style={{
                flex: 1, padding: "10px 10px",
                background: "#f59e0b", color: "#1f2937",
                border: "none", borderRadius: 6, fontSize: 15,
                fontWeight: 700, cursor: "pointer",
              }}
            >
              ⏸ Pause
            </button>
          )}
          <button
            onClick={props.onRestart}
            style={{
              padding: "10px 12px",
              background: "#1e293b", color: "#cbd5e1",
              border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer",
            }}
            title="Restart from frame 0"
          >
            ↺
          </button>
        </div>
        <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Speed
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[0.25, 0.5, 0.75, 1.0].map((s) => (
            <button
              key={s}
              onClick={() => props.onSpeed(s)}
              style={{
                flex: 1, padding: "6px 4px",
                background: props.speed === s ? "#2563eb" : "#1e293b",
                color: "#f8fafc", border: "none", borderRadius: 4,
                fontSize: 12, cursor: "pointer",
              }}
            >
              {s}x
            </button>
          ))}
        </div>
        <div style={{ fontSize: 10, color: "#64748b", marginTop: 8, fontFamily: '"SF Mono", Menlo, monospace' }}>
          frame {props.frameIdx}/{props.frameCount} · {props.playState}
        </div>
      </section>
    </aside>
  );
}


// ── CarrierChipsHeader ──────────────────────────────────────────────


function CarrierChipsHeader({
  carrier, script,
}: { carrier: CarrierProfile; script: DemoScript | null }): JSX.Element {
  return (
    <header
      style={{
        padding: "12px 18px",
        background: "#0f172a",
        borderBottom: "1px solid #1e293b",
        display: "flex", alignItems: "center", gap: 8,
        flexWrap: "wrap",
        flexShrink: 0,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {script?.title ?? "Select a demo"}
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
          {script?.subtitle ?? ""}
        </div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{carrier.settlementBrand}</span>
      <span style={{ fontSize: 12, color: "#64748b" }}>·</span>
      {carrier.supportedDenominations.map((denom) => (
        <span
          key={denom}
          style={{
            fontSize: 10, padding: "3px 7px",
            background: "#1e293b", borderRadius: 999,
            color: "#cbd5e1",
            fontFamily: '"SF Mono", Menlo, monospace',
          }}
        >
          {denom.toUpperCase()}
        </span>
      ))}
      {carrier.loyaltyBalance > 0 && (
        <span
          style={{
            fontSize: 11, padding: "3px 8px",
            background: "#fbbf24", color: "#1f2937",
            borderRadius: 999, fontWeight: 600,
          }}
        >
          {carrier.loyaltyProgramName}: {carrier.loyaltyBalance.toLocaleString()}
        </span>
      )}
      {carrier.secondaryLoyaltyName && carrier.secondaryLoyaltyBalance > 0 && (
        <span
          style={{
            fontSize: 11, padding: "3px 8px",
            background: "#ef4444", color: "#fef2f2",
            borderRadius: 999, fontWeight: 600,
          }}
          title="KDDI ecosystem currency — Ponta — earned/spent at Lawson + partner restaurants on top of au points"
        >
          {carrier.secondaryLoyaltyName}: {carrier.secondaryLoyaltyBalance.toLocaleString()}
        </span>
      )}
      {carrier.groupChatBrand && (
        <span
          style={{
            fontSize: 10, padding: "3px 7px",
            background: "#06c755", color: "#ffffff",
            borderRadius: 999, fontWeight: 600,
          }}
          title="Group chat handoff goes through this app when a demo seals a group plan"
        >
          {carrier.groupChatBrand}
        </span>
      )}
    </header>
  );
}


// ── ChatPanel ───────────────────────────────────────────────────────


function ChatPanel({
  messages, userPrompt, playState,
}: { messages: ChatMessage[]; userPrompt: string; playState: string }): JSX.Element {
  const scrollRef = useRef<HTMLElement | null>(null);
  // Auto-scroll to bottom whenever messages change or stream content
  // updates — so the latest dialogue is always in view without the
  // viewer needing to scroll manually.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages.length, messages[messages.length - 1]?.text.length]);
  // Re-pin to bottom when the container itself resizes (e.g. ending
  // panels mount and shrink the chat area).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <section
      ref={scrollRef as unknown as React.RefObject<HTMLElement>}
      style={{
        flex: 1, minHeight: 0, overflowY: "auto",
        padding: 18, display: "flex", flexDirection: "column", gap: 10,
      }}
    >
      {messages.length === 0 && playState === "idle" && (
        <div
          style={{
            padding: 14, background: "#0f172a", borderRadius: 8,
            border: "1px dashed #334155",
          }}
        >
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6 }}>
            Press ▶ Play in the left rail. The consumer will type:
          </div>
          <div style={{ fontSize: 13, color: "#e2e8f0" }}>
            "{userPrompt}"
          </div>
        </div>
      )}
      {messages.map((m) => (
        <MessageBubble key={m.id} message={m} />
      ))}
    </section>
  );
}


const TONE_COLORS: Record<AgentTone, { ink: string; bg: string; label: string }> = {
  your_twin:    { ink: "#a7f3d0", bg: "#064e3b", label: "#34d399" },
  partner_twin: { ink: "#fbcfe8", bg: "#831843", label: "#f472b6" },
  family_twin:  { ink: "#fde68a", bg: "#78350f", label: "#fbbf24" },
  friend_twin:  { ink: "#bfdbfe", bg: "#1e3a8a", label: "#60a5fa" },
  aristotle:    { ink: "#e9d5ff", bg: "#581c87", label: "#c084fc" },
  broker:       { ink: "#cffafe", bg: "#155e75", label: "#22d3ee" },
  negotiator:   { ink: "#fef3c7", bg: "#92400e", label: "#fbbf24" },
  provider:     { ink: "#e2e8f0", bg: "#334155", label: "#94a3b8" },
  settlement:   { ink: "#bbf7d0", bg: "#14532d", label: "#4ade80" },
  system:       { ink: "#cbd5e1", bg: "#1e293b", label: "#64748b" },
};


function MessageBubble({ message }: { message: ChatMessage }): JSX.Element {
  if (message.kind === "user") {
    return (
      <div
        style={{
          alignSelf: "flex-end", maxWidth: "78%",
          padding: "9px 12px", borderRadius: 12,
          background: "#1d4ed8", color: "#f8fafc",
          fontSize: 13, lineHeight: 1.4,
          animation: "demo-slide-right 0.32s cubic-bezier(0.2, 0.7, 0.3, 1)",
        }}
      >
        {message.text}
      </div>
    );
  }
  if (message.kind === "thinking") {
    return (
      <div
        style={{
          alignSelf: "flex-start", maxWidth: "78%",
          fontStyle: "italic", color: "#94a3b8",
          fontSize: 11, padding: "2px 4px",
          animation: "demo-fade-in 0.4s ease-out",
        }}
      >
        · · · {message.text}
      </div>
    );
  }
  if (message.kind === "agent_dialogue") {
    const tone = message.tone ?? "system";
    const colors = TONE_COLORS[tone];
    // Alternate the side a dialogue bubble enters from so the chat
    // visually reads as back-and-forth between agents. Twin tones
    // and aristotle slide in from the left; broker/negotiator/
    // provider/settlement slide in from the right.
    const rightSide = tone === "broker" || tone === "negotiator"
      || tone === "provider" || tone === "settlement" || tone === "system";
    return (
      <div
        style={{
          alignSelf: rightSide ? "flex-end" : "flex-start",
          maxWidth: "92%",
          padding: "8px 12px", borderRadius: 10,
          background: colors.bg,
          color: colors.ink,
          fontSize: 12, lineHeight: 1.5,
          border: `1px solid ${colors.label}30`,
          animation: rightSide
            ? "demo-slide-right 0.36s cubic-bezier(0.2, 0.7, 0.3, 1)"
            : "demo-slide-left 0.36s cubic-bezier(0.2, 0.7, 0.3, 1)",
        }}
      >
        <div style={{
          fontSize: 10, fontWeight: 700,
          color: colors.label,
          textTransform: "uppercase",
          letterSpacing: 0.5, marginBottom: 3,
        }}>
          {message.speaker}
        </div>
        <div>{message.text}</div>
      </div>
    );
  }
  return (
    <div
      style={{
        alignSelf: "flex-start", maxWidth: "85%",
        padding: "10px 14px", borderRadius: 12,
        background: "#1e293b", color: "#f1f5f9",
        fontSize: 13, lineHeight: 1.5,
        animation: "demo-slide-left 0.36s cubic-bezier(0.2, 0.7, 0.3, 1)",
      }}
    >
      {message.text}
      {message.streaming && (
        <span
          style={{
            display: "inline-block", width: 6, height: 12, marginLeft: 2,
            background: "#60a5fa", animation: "blink 1s infinite",
            verticalAlign: "text-bottom",
          }}
        />
      )}
    </div>
  );
}


// ── GraphPanel ──────────────────────────────────────────────────────


const LANE_HEIGHT = 76;
const NODE_WIDTH = 150;
const NODE_HEIGHT = 50;
const PANEL_WIDTH = 480;
const STATE_FILL: Record<NodeState, string> = {
  idle: "#1f2937", active: "#2563eb", done: "#16a34a", failed: "#dc2626",
};
const STATE_STROKE: Record<NodeState, string> = {
  idle: "#374151", active: "#60a5fa", done: "#4ade80", failed: "#f87171",
};


function GraphPanel({
  state, carrier, usedNodes,
}: { state: PanelState; carrier: CarrierProfile; usedNodes: NodeId[] | null }): JSX.Element {
  // A demo only renders the nodes it actually uses — no point showing
  // Aristotle on a demo that never calls it. Plus carrier-aware
  // wallet filtering: SoftBank's PayPay wallet handles points + fiat
  // in one node; KDDI gets a second Coincheck wallet for crypto.
  const visibleNodes = useMemo(() => {
    const used = usedNodes ? new Set<NodeId>(usedNodes) : null;
    return NODES.filter((id) => {
      // Points live in the carrier wallet — no separate Loyalty node.
      // au PAY + Coincheck are one KDDI wallet — no separate crypto.
      if (id === "loyalty") return false;
      if (id === "crypto_wallet") return false;
      if (used !== null && !used.has(id)) return false;
      return true;
    });
  }, [carrier.id, usedNodes]);
  const pos = useMemo(
    () => layoutByLane(visibleNodes, NODE_DEFINITIONS),
    [visibleNodes],
  );
  return (
    <aside
      style={{
        width: PANEL_WIDTH,
        background: "#0f172a",
        padding: 16,
        borderLeft: "1px solid #1e293b",
        overflowY: "auto",
      }}
      aria-label="Agent execution graph"
    >
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
        Agent graph · live
      </div>
      <svg
        width={PANEL_WIDTH - 32}
        height={LANE_HEIGHT * 6 + 16}
        style={{ display: "block" }}
      >
        {state.edgeFires
          .filter((e) => pos[e.from_node_id] && pos[e.to_node_id])
          .map((e) => (
            <EdgeLine
              key={e.id}
              from={pos[e.from_node_id]}
              to={pos[e.to_node_id]}
            />
          ))}
        {visibleNodes.map((id) => (
          id === "midnight" ? (
            <ProtocolHex
              key={id}
              pos={pos[id]}
              state={state.nodeStates[id]}
              label={state.nodeLabels[id]}
            />
          ) : (
            <NodeRect
              key={id}
              pos={pos[id]}
              state={state.nodeStates[id]}
              label={state.nodeLabels[id]}
            />
          )
        ))}
      </svg>
      <section style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#cbd5e1", marginBottom: 6 }}>
          PACT events {state.pactEvents.length ? `(${state.pactEvents.length})` : ""}
        </div>
        <div
          style={{
            maxHeight: 180, overflowY: "auto",
            background: "#1e293b", borderRadius: 6, padding: 8,
            fontSize: 10, fontFamily: '"SF Mono", Menlo, monospace',
          }}
        >
          {state.pactEvents.length === 0 ? (
            <span style={{ color: "#64748b" }}>(no events yet)</span>
          ) : (
            state.pactEvents.map((e, i) => <PactRow key={i} event={e} />)
          )}
        </div>
      </section>
    </aside>
  );
}


function PactRow({ event }: { event: PactEvent }): JSX.Element {
  const colors: Record<string, string> = {
    "pact.broadcast": "#7dd3fc",
    "pact.bid":       "#fbbf24",
    "pact.drop":      "#f87171",
    "pact.sealed":    "#4ade80",
    "pact.no_proposals": "#94a3b8",
  };
  const color = colors[event.type] ?? "#cbd5e1";
  const name = (event["provider_name"] as string)
    ?? (event["vertical"] as string)
    ?? (event["reason"] as string)
    ?? "";
  const price = (event["final_price_jpy"] ?? event["price_jpy"]) as number | undefined;
  return (
    <div style={{ marginBottom: 2, color: "#cbd5e1" }}>
      <span style={{ color }}>{event.type}</span>{" "}
      <span>{name}</span>
      {price ? <span style={{ color: "#fbbf24" }}> ¥{price.toLocaleString()}</span> : null}
    </div>
  );
}


interface NodePos { x: number; y: number; cx: number; cy: number; w: number; }


function layoutByLane(
  ids: readonly NodeId[],
  defs: Record<NodeId, NodeDefinition>,
): Record<NodeId, NodePos> {
  const byLane: NodeId[][] = [[], [], [], [], [], []];
  for (const id of ids) {
    const lane = defs[id].lane;
    const bucket = byLane[lane];
    if (bucket) bucket.push(id);
  }
  const out: Partial<Record<NodeId, NodePos>> = {};
  const usableWidth = PANEL_WIDTH - 56;
  byLane.forEach((laneIds, laneIdx) => {
    laneIds.forEach((id, slotIdx) => {
      const slotCount = laneIds.length;
      const slotWidth = usableWidth / slotCount;
      const cx = 16 + slotWidth * (slotIdx + 0.5);
      const cy = 24 + LANE_HEIGHT * laneIdx + NODE_HEIGHT / 2;
      // Shrink node width to fit lane density — keeps multi-Twin
      // lanes from overlapping when 4-6 nodes share a row.
      const w = Math.min(NODE_WIDTH, Math.max(58, slotWidth - 6));
      out[id] = { x: cx - w / 2, y: cy - NODE_HEIGHT / 2, cx, cy, w };
    });
  });
  return out as Record<NodeId, NodePos>;
}


// Midnight is a protocol, not an agent — render it as a hexagonal
// "rail" indicator that's visually distinct from the agent nodes.
function ProtocolHex({
  pos, state, label,
}: { pos: NodePos; state: NodeState; label: string }): JSX.Element {
  const fill = state === "idle" ? "#0b1220" : STATE_FILL[state];
  const stroke = state === "idle" ? "#475569" : STATE_STROKE[state];
  const w = pos.w;
  const h = NODE_HEIGHT;
  // hexagon path centered at (cx, cy)
  const cx = pos.cx;
  const cy = pos.cy;
  const dx = w / 2;
  const dy = h / 2;
  const pts = [
    `${cx - dx * 0.85},${cy - dy}`,
    `${cx + dx * 0.85},${cy - dy}`,
    `${cx + dx},${cy}`,
    `${cx + dx * 0.85},${cy + dy}`,
    `${cx - dx * 0.85},${cy + dy}`,
    `${cx - dx},${cy}`,
  ].join(" ");
  return (
    <g>
      <polygon
        points={pts}
        fill={fill}
        stroke={stroke}
        strokeWidth={1.5}
        strokeDasharray={state === "idle" ? "4 3" : undefined}
      />
      <text
        x={pos.cx}
        y={pos.cy - 4}
        textAnchor="middle"
        fill="#fbbf24"
        fontSize={9}
        fontWeight={700}
        letterSpacing={1}
      >
        PROTOCOL
      </text>
      <text
        x={pos.cx}
        y={pos.cy + 12}
        textAnchor="middle"
        fill="#f8fafc"
        fontSize={pos.w < 90 ? 10 : 11}
        fontWeight={600}
      >
        {(() => {
          const maxChars = Math.max(6, Math.floor(pos.w / 6.5));
          return label.length > maxChars ? label.slice(0, maxChars - 1) + "…" : label;
        })()}
      </text>
      {state === "active" && (
        <circle cx={cx} cy={cy - h / 2 - 4} r={3} fill="#fbbf24">
          <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" repeatCount="indefinite" />
        </circle>
      )}
    </g>
  );
}


function NodeRect({
  pos, state, label,
}: { pos: NodePos; state: NodeState; label: string }): JSX.Element {
  return (
    <g>
      <rect
        x={pos.x}
        y={pos.y}
        width={pos.w}
        height={NODE_HEIGHT}
        rx={6}
        fill={STATE_FILL[state]}
        stroke={STATE_STROKE[state]}
        strokeWidth={1}
      />
      <text
        x={pos.cx}
        y={pos.cy - 3}
        textAnchor="middle"
        fill="#f8fafc"
        fontSize={pos.w < 90 ? 9 : 10}
        fontWeight={600}
      >
        {(() => {
          // Truncate proportionally to the node's actual rendered width
          // so narrow lanes (multi-Twin demos) get tighter labels.
          const maxChars = Math.max(6, Math.floor(pos.w / 7));
          return label.length > maxChars ? label.slice(0, maxChars - 1) + "…" : label;
        })()}
      </text>
      <text
        x={pos.cx}
        y={pos.cy + 11}
        textAnchor="middle"
        fill="#94a3b8"
        fontSize={8}
      >
        {state}
      </text>
      {state === "active" && (
        <circle cx={pos.x + 7} cy={pos.y + 7} r={3} fill="#60a5fa">
          <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" repeatCount="indefinite" />
        </circle>
      )}
    </g>
  );
}


function EdgeLine({ from, to }: { from: NodePos; to: NodePos }): JSX.Element {
  const fromY = from.cy + NODE_HEIGHT / 2 - 2;
  const toY = to.cy - NODE_HEIGHT / 2 + 2;
  return (
    <g>
      <line x1={from.cx} y1={fromY} x2={to.cx} y2={toY}
            stroke="#60a5fa" strokeWidth={1.5} opacity={0.4} />
      <circle r={4} fill="#fbbf24">
        <animateMotion
          dur="1s"
          repeatCount="1"
          fill="freeze"
          path={`M${from.cx},${fromY} L${to.cx},${toY}`}
        />
      </circle>
    </g>
  );
}


// ── EndingPanels ────────────────────────────────────────────────────


interface EndingPanelsProps {
  script: DemoScript;
  carrier: CarrierProfile;
  showReceipt: boolean;
  showTwinUpdate: boolean;
  showPivot: boolean;
}


function EndingPanels({
  script, carrier, showReceipt, showTwinUpdate, showPivot,
}: EndingPanelsProps): JSX.Element {
  const e = script.endings;
  return (
    <section
      style={{
        padding: "12px 18px 18px",
        background: "#0b1220",
        borderTop: "1px solid #1e293b",
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr 1fr",
        gap: 10,
        maxHeight: "42vh",
        overflowY: "auto",
        flexShrink: 0,
      }}
    >
      {showReceipt && (
        <Panel title="Provable Receipt" tone="green">
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{e.receipt.receipt_id}</div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>
            {e.receipt.action_summary}
          </div>
          <div style={{ fontSize: 10, fontFamily: '"SF Mono", Menlo, monospace', color: "#fbbf24", marginTop: 6 }}>
            ⛓ midnight: {e.receipt.midnight_anchor_hex.slice(0, 16)}…
          </div>
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 11, cursor: "pointer", color: "#cbd5e1" }}>Rationale ({e.receipt.rationale_steps.length})</summary>
            <ol style={{ fontSize: 10, color: "#cbd5e1", paddingLeft: 16, marginTop: 4 }}>
              {e.receipt.rationale_steps.map((step, i) => (<li key={i}>{step}</li>))}
            </ol>
          </details>
          <details style={{ marginTop: 4 }}>
            <summary style={{ fontSize: 11, cursor: "pointer", color: "#cbd5e1" }}>Alternatives considered ({e.receipt.alternatives_considered.length})</summary>
            <ul style={{ fontSize: 10, color: "#94a3b8", paddingLeft: 16, marginTop: 4 }}>
              {e.receipt.alternatives_considered.map((alt, i) => (<li key={i}>{alt}</li>))}
            </ul>
          </details>
          <details style={{ marginTop: 4 }}>
            <summary style={{ fontSize: 11, cursor: "pointer", color: "#cbd5e1" }}>
              JFSA tax export (CSV preview)
            </summary>
            <pre style={{
              fontSize: 9, fontFamily: '"SF Mono", Menlo, monospace',
              color: "#cbd5e1", marginTop: 4, whiteSpace: "pre-wrap",
              background: "#020617", padding: 6, borderRadius: 4,
              overflowX: "auto",
            }}>
{e.receipt.jfsa_csv_preview}
            </pre>
          </details>
        </Panel>
      )}
      {showTwinUpdate && (
        <Panel title="Twin update" tone="blue">
          <div style={{ fontSize: 11, color: "#94a3b8" }}>
            {e.twinUpdate.category} / {e.twinUpdate.key}
          </div>
          <div style={{ fontSize: 12, marginTop: 6 }}>
            <span style={{ color: "#94a3b8" }}>before:</span> {e.twinUpdate.old_value}
          </div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            <span style={{ color: "#94a3b8" }}>after:</span>{" "}
            <span style={{ color: "#4ade80" }}>{e.twinUpdate.new_value}</span>
          </div>
          <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 6, fontStyle: "italic" }}>
            {e.twinUpdate.reason}
          </div>
          <div style={{ fontSize: 11, marginTop: 6, color: "#fbbf24" }}>
            confidence {e.twinUpdate.confidence_change >= 0 ? "+" : ""}{e.twinUpdate.confidence_change.toFixed(2)}
          </div>
        </Panel>
      )}
      {showPivot && (
        <Panel title="Cross-vertical pivot" tone="amber">
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {e.pivot.headline}
          </div>
          <ul style={{ paddingLeft: 0, marginTop: 8, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {e.pivot.suggestions.map((sug, i) => (
              <PivotCta key={i} suggestion={sug} carrier={carrier} />
            ))}
          </ul>
        </Panel>
      )}
    </section>
  );
}


interface PivotCtaProps {
  suggestion: { vertical: string; description: string; ctaLabel: string };
  carrier: CarrierProfile;
}


function PivotCta({ suggestion, carrier }: PivotCtaProps): JSX.Element {
  const [added, setAdded] = useState(false);
  return (
    <li style={{
      fontSize: 11, padding: "6px 8px",
      background: "#0f172a", borderRadius: 4,
      border: added ? "1px solid #16a34a" : "1px solid #1e293b",
    }}>
      <div style={{ color: "#94a3b8", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {suggestion.vertical}
      </div>
      <div style={{ marginTop: 2, color: "#e2e8f0" }}>
        {suggestion.description}
      </div>
      {added ? (
        <div style={{
          marginTop: 6, padding: "5px 8px",
          background: "#14532d", color: "#bbf7d0",
          borderRadius: 4, fontSize: 10, fontWeight: 600,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span style={{ color: "#4ade80" }}>✓</span>
          Added to {carrier.settlementBrand} Wallet · pending
          <button
            onClick={() => setAdded(false)}
            style={{
              marginLeft: "auto", padding: "2px 6px",
              background: "transparent", color: "#94a3b8",
              border: "1px solid #334155", borderRadius: 3,
              fontSize: 9, cursor: "pointer",
            }}
          >
            Undo
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdded(true)}
          style={{
            marginTop: 6, padding: "4px 8px",
            background: "#2563eb", color: "#f8fafc",
            border: "none", borderRadius: 4,
            fontSize: 10, fontWeight: 600, cursor: "pointer",
          }}
        >
          {suggestion.ctaLabel} via {carrier.settlementBrand}
        </button>
      )}
    </li>
  );
}


function Panel({
  title, tone, children,
}: { title: string; tone: "green" | "blue" | "amber"; children: ReactNode }): JSX.Element {
  const border = tone === "green" ? "#16a34a" : tone === "blue" ? "#2563eb" : "#fbbf24";
  return (
    <div style={{
      background: "#0f172a",
      border: `1px solid ${border}`,
      borderRadius: 8,
      padding: 12,
      minWidth: 0,
    }}>
      <div style={{
        fontSize: 10, color: border, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}
